import { Component, ChangeDetectionStrategy, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';

import { Ticket } from '../../models/ticket.models';
import {
  getPriorityColor,
  getPriorityBadgeClass,
  getStatusBadgeClass,
  shortenStatusLabel,
  formatRelativeTime,
} from '../../utils/ticket-helpers';

@Component({
  selector: 'app-case-card',
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      class="case-card d-flex align-items-start p-3 mb-1 rounded-1"
      [class.active]="active()"
      [style.border-left]="'4px solid ' + priorityColor()"
      (click)="cardClicked.emit(ticket())"
      role="button"
      tabindex="0"
    >
      <div class="flex-grow-1 min-width-0">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <span class="text-muted small fw-semibold">#{{ ticket().request_id }}</span>
          <span class="text-muted small">{{ relativeTime() }}</span>
        </div>
        <div class="subject-line mb-2">{{ ticket().subject }}</div>
        <div class="d-flex flex-wrap gap-1 align-items-center">
          <span class="badge" [class]="priorityBadge()">{{ ticket().priority }}</span>
          <span class="badge" [class]="statusBadge()">{{ statusLabel() }}</span>
          @if (ticket().issue_type) {
            <span class="text-muted small">{{ ticket().issue_type }}</span>
          }
        </div>
      </div>
      <div class="d-flex align-items-center ms-2 text-muted">
        ›
      </div>
    </div>
  `,
  styles: [`
    .case-card {
      cursor: pointer;
      transition: background-color 0.15s ease;
      background-color: transparent;
      border: 1px solid transparent;
    }
    .case-card:hover {
      background-color: rgba(0, 0, 0, 0.03);
    }
    .case-card.active {
      background-color: #fff0ed;
      border-color: rgba(0, 0, 0, 0.05);
    }
    .subject-line {
      font-size: 0.875rem;
      font-weight: 500;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .min-width-0 {
      min-width: 0;
    }
    :host ::ng-deep .bg-purple {
      background-color: #7c3aed;
      color: #fff;
    }
  `],
})
export class CaseCardComponent {
  ticket = input.required<Ticket>();
  active = input<boolean>(false);
  cardClicked = output<Ticket>();

  priorityColor(): string {
    return getPriorityColor(this.ticket().priority);
  }

  priorityBadge(): string {
    return getPriorityBadgeClass(this.ticket().priority);
  }

  statusBadge(): string {
    return getStatusBadgeClass(this.ticket().ticket_status);
  }

  statusLabel(): string {
    return shortenStatusLabel(this.ticket().ticket_status);
  }

  relativeTime(): string {
    return formatRelativeTime(this.ticket().created_time);
  }
}
